import { takeEvery, put, all, call, select } from 'redux-saga/effects'
import { actions } from './ViewsSlice'
import { logout } from 'src/features/session/SessionActions'
import FW5MLError from 'src/services/FW5ML/FW5MLError'
import { ACCESS_DENIED } from 'src/services/FW5ML/errorCodes'
import { notificationsEnqueue } from 'src/features/notifications/NotificationsSlice'
import { history } from 'src/app/Router'
import { historyHandler, checkIfArray } from 'src/utils'
import { LOCATION_CHANGE } from 'connected-react-router'
import { actions as configActions } from 'src/features/config/ConfigSlice'
import { siteName } from 'src/utils/oneCodeBase'
import { getConfig } from 'src/features/config/ConfigSelector'
import { getSetting } from 'src/features/mySettings/MySettingsSelector'

function* requestView(services, { payload = [] }) {
  const views = payload

  const StoriesRepository = services('StoriesRepository')
  const StoryParser = services('StoryParser')

  if (!views || views.length === 0 || !checkIfArray(views)) return

  try {
    const medicalAbstracts = yield select(
      getSetting('include_medical_abstract')
    )
    const payload = {
      content_views: views.map(({ key, ...rest }) => rest),
      include_medical_abstract: medicalAbstracts ?? false
    }
    let method = 'listArticles'

    if (siteName === 'Reports') {
      const { views = {} } = yield select(getConfig('riverConfig'))
      const { publicationDate = null, publicationDateEnd = null } = views

      // Pass config values to search parser
      payload.publication_date = publicationDate
      payload.publication_date_end = publicationDateEnd

      method = 'listReportArticles'
    }

    const { data: articles } = yield call([StoriesRepository, method], payload)

    let normalizedArticles = yield StoryParser.normalizeViewSet(articles)
    normalizedArticles = normalizedArticles
      .map(({ name, data }, index) => {
        const originalView = views[index]
        const { key } = originalView
        const returnKey = key || name
        return {
          [returnKey]: { stories: data }
        }
      })
      .reduce((prev, curr) => {
        const curKey = Object.keys(curr)[0]
        const curValue = Object.values(curr)[0]
        let key = curKey
        // This provides a way to query multiple instances of the same content
        // view with different parameters in the same request.
        while (prev[key] !== undefined) {
          key += '(copy)'
        }
        return {
          ...prev,
          [key]: curValue
        }
      }, {})

    yield put(actions.successViews(normalizedArticles))
  } catch (e) {
    if (e instanceof FW5MLError) {
      const code = e.getCode()
      if (code === ACCESS_DENIED) {
        yield put(logout({ reload: true }))
        return
      }
    }
    yield put(actions.errorViews(e.message))
    throw e
  }
}

function* requestSingleStoryViews(services, { payload }) {
  const { views, articleId } = payload
  const StoriesRepository = services('StoriesRepository')
  const StoryParser = services('StoryParser')

  if (!views || views?.length === 0 || !checkIfArray(views) || !articleId) {
    return
  }

  try {
    const medicalAbstracts = yield select(
      getSetting('include_medical_abstract')
    )
    const payload = {
      content_views: views.map(({ key, ...rest }) => rest),
      include_medical_abstract: medicalAbstracts ?? false
    }
    let method = 'listArticleViews'

    const { data: articles } = yield call(
      [StoriesRepository, method],
      articleId,
      payload
    )

    let normalizedArticles = yield StoryParser.normalizeViewSet(articles)
    normalizedArticles = normalizedArticles
      .map(({ name, data }, index) => {
        const originalView = views[index]
        const { key } = originalView
        const returnKey = key || name
        return {
          [returnKey]: { stories: data }
        }
      })
      .reduce((prev, curr) => {
        const curKey = Object.keys(curr)[0]
        const curValue = Object.values(curr)[0]
        let key = curKey
        // This provides a way to query multiple instances of the same content
        // view with different parameters in the same request.
        while (prev[key] !== undefined) {
          key += '(copy)'
        }
        return {
          ...prev,
          [key]: curValue
        }
      }, {})

    yield put(actions.successViews(normalizedArticles))
  } catch (e) {
    if (e instanceof FW5MLError) {
      const code = e.getCode()
      if (code === ACCESS_DENIED) {
        yield put(logout({ reload: true }))
        return
      }
    }
    yield put(actions.errorViews(e.message))
    throw e
  }
}

function* resetTemplateViews(services, { payload = {} }) {
  try {
    yield put(
      configActions.addConfigRequest({
        key: 'templateViews',
        data: {
          desktop: [],
          mobile: []
        }
      })
    )
  } catch (e) {
    console.log(e)
  }
}

function* redirect(services, { payload = null }) {
  const StoriesRepository = services('StoriesRepository')
  const fw5_story_id = yield call(
    [StoriesRepository, 'resolveLegacyLinks'],
    payload
  )
  if (fw5_story_id) {
    yield put(actions.redirectSuccess(fw5_story_id))
  } else {
    yield notificationsEnqueue({
      message: 'FW Story not found'
    })
    historyHandler(`/contact-us`, '', history, 'push')
  }
}

export default function* watchViews(services) {
  yield all([
    takeEvery(actions.requestViews.toString(), requestView, services),
    takeEvery(
      actions.requestSingleStoryViews.toString(),
      requestSingleStoryViews,
      services
    ),
    takeEvery(actions.redirectRequest.toString(), redirect, services),
    takeEvery(LOCATION_CHANGE, resetTemplateViews, services)
  ])
}
